import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import type { ZodEnum, ZodLiteral, ZodString, ZodUnion } from 'zod';
import { z } from 'zod';

import { useEffectAfterRender, useRefValue } from 'helpers/hooks';
import type { IForms } from 'types/form';
import type { IModals } from 'types/modal';
import type { SearchParamsActions } from 'types/router';

type ActionHandler = (action: {
  action: SearchParamValue;
  form: SearchParamValue<IForms['type']>;
  modal: SearchParamValue<IModals['type']>;
}) => void;

type ActionTrigger = 'form' | 'formAction' | 'modal' | 'modalAction';

type SearchParamValue<T extends string = string> = T | null | undefined;

/**
 * @link https://ourcodeworld.com/articles/read/608/how-to-camelize-and-decamelize-strings-in-javascript
 */
const camelize = (text: string): string =>
  text.replace(/^([A-Z])|[\s-_]+(\w)/g, (match, p1, p2) =>
    p2 ? p2.toUpperCase() : p1.toLowerCase(),
  );

/**
 * @link https://ourcodeworld.com/articles/read/608/how-to-camelize-and-decamelize-strings-in-javascript
 */
const decamelize = <T extends string>(text: T, separator: string): T =>
  text
    .replace(/([a-z\d])([A-Z])/g, `$1${separator}$2`)
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, `$1${separator}$2`)
    .toLowerCase() as T;

const getCamelCasedValue = <T extends string = string>(
  value: SearchParamValue,
): SearchParamValue<T> =>
  value
    ? (camelize(value) as SearchParamValue<T>)
    : (value as SearchParamValue<T>);

export const useSearchParamsAction = (
  callback: ActionHandler,
  triggers: ActionTrigger[],
  deps: ReadonlyArray<any> = [],
) => {
  const search = useSearch({
    strict: false,
  });
  const action = 'action' in search ? search.action : undefined;
  const formParam = 'form' in search ? search.form : undefined;
  const modalParam = 'modal' in search ? search.modal : undefined;

  const form = getCamelCasedValue<IForms['type']>(formParam);
  const modal = getCamelCasedValue<IModals['type']>(modalParam);

  const refCallback = useRefValue(callback);
  const refTriggers = useRef(triggers);

  // Do not pass dependencies as it would cause this method to run every time
  // we open form or modal, leading to the callback being called multiple times
  // and causing state bugs.
  const triggerSearchParamsAction = useCallback(() => {
    const canTriggerAction = (value: ActionTrigger): boolean => {
      switch (value) {
        case 'form':
          return Boolean(form && !action);
        case 'formAction':
          return Boolean(form && action);
        case 'modal':
          return Boolean(modal && !action);
        case 'modalAction':
          return Boolean(modal && action);
        default:
          return false;
      }
    };

    if (refTriggers.current.some(canTriggerAction)) {
      refCallback.current({ action, form, modal });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    triggerSearchParamsAction();
  }, [triggerSearchParamsAction]);

  return {
    triggerSearchParamsAction,
  };
};

export const useSearchParamsActionableState = (
  type: Extract<keyof SearchParamsActions, 'form' | 'modal'>,
  atom?: IForms | IModals,
) => {
  const navigate = useNavigate();
  const search = useSearch({
    strict: false,
  });
  const formParam = 'form' in search ? search.form : undefined;
  const modalParam = 'modal' in search ? search.modal : undefined;
  const formOrModal = formParam || modalParam;

  useEffectAfterRender(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        action:
          // eslint-disable-next-line no-nested-ternary
          atom?.type && atom.action
            ? atom.action
            : !formOrModal
              ? undefined
              : // @ts-ignore
                prev.action,
        form:
          atom?.type && type === 'form'
            ? decamelize(atom.type, '-')
            : // @ts-ignore
              prev.form,
        modal:
          atom?.type && type === 'modal'
            ? decamelize(atom.type, '-')
            : // @ts-ignore
              prev.modal,
      }),
    });
  }, [atom?.type]);
};

/** Booleans round-trip the URL as 'true'/'false' strings (qs does not
 * coerce), so accept both forms and normalize to real booleans. */
const zLooseBoolean = z.union([
  z.boolean(),
  z.enum(['true', 'false']).transform((val) => val === 'true'),
]);

/** Boolean twin of zFiltersArray — for filters whose values are booleans
 * (e.g. warm), where a single-value URL param arrives as a bare scalar. */
export const zBooleanFiltersArray = () =>
  z
    .union([
      z.array(zLooseBoolean),
      zLooseBoolean.transform((val) => [val]),
      z.undefined().transform(() => []),
    ])
    .default([])
    .optional()
    .catch([]);

export const zFiltersArray = <
  SchemaType extends
    | ZodEnum<[string, ...string[]]>
    | ZodString
    | ZodUnion<[ZodLiteral<string>, ...ZodLiteral<string>[]]> = ZodString,
>(
  schema?: SchemaType,
) => {
  const baseSchema = schema ?? z.string();

  return z
    .union([
      // Case 1: Already an array
      z.array(baseSchema),
      // Case 2: Single value -> transformed into array if truthy
      baseSchema.transform((val) => [val]).pipe(z.array(baseSchema)),
      // Case 3: undefined -> transformed into empty array
      z.undefined().transform(() => []),
    ])
    .default([])
    .optional()
    .catch([]);
};
